import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export function useCustomerMetrics(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-metrics', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) return null;

      // Fetch all related data
      const [
        customer,
        jobs,
        leads,
        communications
      ] = await Promise.all([
        supabase.from('customers').select('*').eq('id', customerId).single(),
        supabase.from('jobs').select('*').eq('customer_id', customerId),
        supabase.from('leads').select('*').eq('customer_id', customerId),
        supabase.from('customer_communications').select('*').eq('customer_id', customerId)
      ]);

      if (customer.error) throw customer.error;

      const jobsData = jobs.data || [];
      const leadsData = leads.data || [];
      const commsData = communications.data || [];

      // Calculate job metrics
      const completedJobs = jobsData.filter(j => j.status === 'completed').length;
      const cancelledJobs = jobsData.filter(j => j.status === 'cancelled').length;
      const activeJobs = jobsData.filter(j => 
        j.status !== 'completed' && j.status !== 'cancelled'
      ).length;

      // Calculate lead metrics
      const convertedLeads = leadsData.filter(l => l.status === 'converted').length;
      const conversionRate = leadsData.length > 0 
        ? (convertedLeads / leadsData.length) * 100 
        : 0;

      // Calculate financial metrics
      const totalPaid = jobsData.reduce((sum, job) => sum + (job.amount_paid || 0), 0);
      const outstandingBalance = jobsData.reduce((sum, job) => 
        sum + (job.amount_outstanding || 0), 0
      );
      const averageJobValue = completedJobs > 0
        ? customer.data.total_lifetime_value / completedJobs
        : 0;

      // Calculate payment reliability
      const paymentReliability = (() => {
        if (jobsData.length === 0) return 'good';
        const paidJobsRatio = jobsData.filter(j => 
          j.payment_status === 'paid'
        ).length / jobsData.length;
        
        if (paidJobsRatio >= 0.9) return 'excellent';
        if (paidJobsRatio >= 0.7) return 'good';
        if (paidJobsRatio >= 0.5) return 'fair';
        return 'poor';
      })();

      // Find last contact date
      const allDates = [
        ...leadsData.map(l => l.last_contact_date).filter(Boolean),
        ...commsData.map(c => c.created_at).filter(Boolean),
        customer.data.last_contact_date
      ].filter(Boolean);
      
      const lastContactDate = allDates.length > 0
        ? allDates.sort().reverse()[0]
        : undefined;

      return {
        totalLifetimeValue: customer.data.total_lifetime_value || 0,
        totalJobs: jobsData.length,
        completedJobs,
        cancelledJobs,
        activeJobs,
        totalLeads: leadsData.length,
        convertedLeads,
        conversionRate,
        averageJobValue,
        outstandingBalance,
        totalPaid,
        totalCommunications: commsData.length,
        lastContactDate,
        paymentReliability: paymentReliability as 'excellent' | 'good' | 'fair' | 'poor'
      };
    }
  });
}